import { EventBus } from '../../../../shared/EventBus';
import type { AbilityMetadata } from './AbilityRegistry';
import type { AbilityFunction } from './AbilityBuilderTypes';
import type { ActionContext } from './types';
import type { AbilityBehavior } from './behaviors/types';
import type { BuildSnapshot } from './AbilityBuilderFactory';
import { registerAbilitySessionReleaseHook } from './AbilitySessionLifecycle';

/** Drain the canonical mana field; preserved for existing sustained abilities. */
export function applyResourceDrain(player: { mana?: number }, drainPerSecond: number): boolean {
  const current = player.mana ?? 0;
  if (current <= 0) return false;
  player.mana = current - drainPerSecond;
  return player.mana <= 0;
}

/** The existing per-player toggle/session owner, separated from construction. */
export function createToggleAbility(abilityId: string, metadata: Partial<AbilityMetadata>,
  toggleCfg: NonNullable<BuildSnapshot['toggleConfig']>, behaviors: AbilityBehavior[]): AbilityFunction {
  const eventBus = EventBus.getInstance();
  interface ToggleState {
    isActive: boolean;
    drainInterval: ReturnType<typeof setInterval> | null;
    durationTimer: ReturnType<typeof setTimeout> | null;
  }
  interface ToggleSessionState extends ToggleState {
    playerObject: object;
    playerId: string;
    /** Context that activated the toggle, so a session release can deactivate it. */
    activeCtx: ActionContext | null;
  }
  const stateById = new Map<string, ToggleSessionState>();
  const stateByPlayer = new WeakMap<object, ToggleSessionState>();
  let unregisterReleaseHook: (() => void) | null = null;

  const deactivate = (playerId: string, ctx: ActionContext) => {
    const state = stateById.get(playerId);
    if (!state || !state.isActive) return;
    state.isActive = false;
    state.activeCtx = null;
    toggleCfg.onDeactivate?.(ctx);
    eventBus.emit('vfx:status_effect', { targetId: playerId, effectId: abilityId, active: false });
    if (state.drainInterval) { clearInterval(state.drainInterval); state.drainInterval = null; }
    if (state.durationTimer) { clearTimeout(state.durationTimer); state.durationTimer = null; }
  };

  const fn: AbilityFunction = (ctx: ActionContext) => {
    const playerId = ctx.currentPlayer.id;
    const playerObject = ctx.currentPlayer as object;
    let state = stateByPlayer.get(playerObject);
    if (state && state.playerId !== playerId) {
      stateById.delete(state.playerId);
      state.playerId = playerId;
      stateById.set(playerId, state);
    }
    state ??= stateById.get(playerId);
    if (!state) {
      state = { playerObject, playerId, isActive: false, drainInterval: null, durationTimer: null, activeCtx: null };
      stateById.set(playerId, state);
      stateByPlayer.set(playerObject, state);
      unregisterReleaseHook ??= registerAbilitySessionReleaseHook(releasePlayer);
    }

    if (state.isActive) {
      deactivate(playerId, ctx);
      return;
    }

    state.isActive = true;
    state.activeCtx = ctx;
    toggleCfg.onActivate?.(ctx);
    eventBus.emit('vfx:status_effect', { targetId: playerId, effectId: abilityId, active: true });

    if (toggleCfg.resourceDrainPerSecond) {
      state.drainInterval = setInterval(() => {
        if (applyResourceDrain(ctx.currentPlayer, toggleCfg.resourceDrainPerSecond!)) {
          deactivate(playerId, ctx);
        }
      }, 1000);
    }

    if (toggleCfg.maxDurationMs) {
      state.durationTimer = setTimeout(() => deactivate(playerId, ctx), toggleCfg.maxDurationMs);
    }

    for (const behavior of behaviors) {
      behavior.execute(ctx, metadata as AbilityMetadata);
    }
  };
  (fn as any).metadata = metadata;
  const releasePlayer = (playerId?: string) => {
    const ids = playerId ? [playerId] : [...stateById.keys()];
    for (const id of ids) {
      const state = stateById.get(id);
      if (!state) continue;
      // Releasing a session mid-toggle is still a deactivation. Dropping the
      // timers alone skipped `onDeactivate` and left `vfx:status_effect`
      // switched ON, so a reconnect stranded the aura on the character with
      // nothing left to turn it off.
      if (state.isActive && state.activeCtx) deactivate(id, state.activeCtx);
      if (state.drainInterval) clearInterval(state.drainInterval);
      if (state.durationTimer) clearTimeout(state.durationTimer);
      stateById.delete(id);
      stateByPlayer.delete(state.playerObject);
    }
    if (stateById.size === 0 && unregisterReleaseHook) {
      unregisterReleaseHook();
      unregisterReleaseHook = null;
    }
  };
  (fn as any).releasePlayer = releasePlayer;
  return fn;
}
